import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ApiError, apiErrorCode, apiErrorMessage } from '../../lib/api';
import {
  buildWikiTree,
  descendantIds,
  patchWikiPage,
  WIKI_SLUG_RE,
  wikiRoute,
  type WikiPageFull,
  type WikiPageSlim,
  type WikiSpaceRef,
} from '../../lib/wiki';
import { cn } from '../../lib/utils';
import WikiPageTree from './WikiPageTree';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  space: WikiSpaceRef;
  spaceName: string;
  page: WikiPageFull;
  pages: WikiPageSlim[];
  onMoved: (page: WikiPageFull) => void;
};

/** Screen 7: pick a new parent; the subtree follows. Slug field appears only on a 409. */
export default function MovePageDialog({ open, onOpenChange, space, spaceName, page, pages, onMoved }: Props) {
  const [parentId, setParentId] = useState<string | null>(page.parent_id);
  const [slug, setSlug] = useState(page.slug);
  const [needSlug, setNeedSlug] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setParentId(page.parent_id);
      setSlug(page.slug);
      setNeedSlug(false);
      setError(null);
    }
  }, [open, page.parent_id, page.slug]);

  const excluded = useMemo(() => descendantIds(pages, page.id), [pages, page.id]);
  const tree = useMemo(() => buildWikiTree(pages), [pages]);
  const byId = useMemo(() => new Map(pages.map((row) => [row.id, row])), [pages]);
  const childCount = excluded.size - 1;
  const parent = parentId ? byId.get(parentId) : undefined;
  const newPath = parent ? `${parent.path}/${slug}` : slug;
  const unchanged = parentId === page.parent_id && slug === page.slug;
  const slugValid = WIKI_SLUG_RE.test(slug);
  const canSubmit = !busy && !unchanged && slugValid;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const body: { version: number; parent_id?: string | null; slug?: string } = { version: page.version };
      if (parentId !== page.parent_id) body.parent_id = parentId;
      if (slug !== page.slug) body.slug = slug;
      const moved = await patchWikiPage(space, page.id, body);
      onOpenChange(false);
      onMoved(moved);
    } catch (err) {
      if (err instanceof ApiError && apiErrorCode(err) === 'CONFLICT') {
        setNeedSlug(true);
        setError('A page with this slug already exists at the destination. Change the slug to continue.');
      } else {
        setError(apiErrorMessage(err, 'Failed to move page.'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move “{page.title}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">New parent</p>
          <div className="max-h-64 overflow-y-auto rounded-md border p-2">
            <button
              type="button"
              className={cn(
                'mb-1 w-full rounded-md px-2 py-1 text-left text-sm hover:bg-accent/70',
                parentId === null && 'bg-primary/10 font-medium text-primary',
              )}
              onClick={() => setParentId(null)}
            >
              {spaceName} (space root)
            </button>
            <WikiPageTree
              nodes={tree}
              selectedId={parentId}
              disabledIds={excluded}
              expandPathTo={excluded}
              onSelect={(id) => setParentId(id)}
            />
          </div>
          {needSlug ? (
            <div className="space-y-1">
              <Label htmlFor="wiki-move-slug">Slug</Label>
              <Input
                id="wiki-move-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                maxLength={64}
                aria-invalid={!slugValid}
              />
            </div>
          ) : null}
          <p className="truncate text-xs text-muted-foreground" title={wikiRoute(space, newPath)}>
            New URL {wikiRoute(space, newPath)}
          </p>
          {childCount > 0 ? (
            <p className="text-xs text-muted-foreground">
              {childCount} child page{childCount === 1 ? '' : 's'} move{childCount === 1 ? 's' : ''} with it.
            </p>
          ) : null}
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? 'Moving…' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
